import companyLogo from "../assets/images/logo/service_amylogo.png";

const PRINT_FRAME_ID = "report-html-print-frame";

// Webpack gives asset paths relative to the page; the print frame starts on
// about:blank, so absolutise the logo before it goes into its markup.
function logoUrl() {
    try {
        return new URL(companyLogo, window.location.href).href;
    } catch (err) {
        return companyLogo;
    }
}

// Same masthead the PDF reports carry, so a printed sheet and a downloaded PDF
// look like the same document.
function sheetHead(title) {
    return `<div class="sheet-head">
        <img src="${logoUrl()}" alt="" />
        ${title ? `<h1>${escapeHtml(title)}</h1>` : ""}
    </div>`;
}

const BASE_STYLES = `
    /* A zero page margin is what suppresses the browser's own header/footer
       (page title, date, URL); the real margin comes from the body padding. */
    @page { margin: 0; }
    body {
        font-family: Arial, Helvetica, sans-serif;
        font-size: 11px;
        color: #000;
        margin: 0;
        padding: 10mm;
        /* Keep header shading and badges when the browser's "background
           graphics" option is off, which is its default. */
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
    .sheet-head { text-align: center; margin-bottom: 10px; }
    .sheet-head img { max-height: 55px; margin-bottom: 6px; }
    h1 { font-size: 16px; margin: 0; text-transform: uppercase; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #000; padding: 4px 6px; }
    th { background: #e8e8e8; }
    tr { page-break-inside: avoid; }
    thead { display: table-header-group; }
`;

function escapeHtml(value) {
    if (value === null || value === undefined) {
        return "";
    }
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

// Writes a standalone document into a hidden iframe and prints it. The frame is
// kept until the next print so the browser is not left printing a detached
// document.
export function printHtmlDocument(html) {
    const old = document.getElementById(PRINT_FRAME_ID);
    if (old) {
        old.remove();
    }

    const frame = document.createElement("iframe");
    frame.id = PRINT_FRAME_ID;
    frame.style.position = "fixed";
    frame.style.left = "-10000px";
    frame.style.top = "0";
    frame.style.width = "800px";
    frame.style.height = "600px";
    frame.style.border = "0";
    document.body.appendChild(frame);

    const doc = frame.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    // Printing before the logo has decoded leaves a blank box, so wait it out.
    const images = Array.from(doc.images);
    Promise.all(
        images.map((img) =>
            img.complete
                ? Promise.resolve()
                : new Promise((resolve) => {
                    img.onload = resolve;
                    img.onerror = resolve;
                })
        )
    ).then(() => {
        setTimeout(() => {
            frame.contentWindow.focus();
            frame.contentWindow.print();
        }, 200);
    });
}

// Prints one section of the page (a report body) instead of the whole
// dashboard. Bootstrap is not loaded in the print frame, so the few classes the
// reports rely on are restated here.
export function printElement(node, { title = "", orientation = "portrait" } = {}) {
    if (!node) {
        return;
    }

    const html = `<!DOCTYPE html>
<html>
<head>
<title>${escapeHtml(title)}</title>
<style>
    @page { size: A4 ${orientation}; }
    ${BASE_STYLES}
    table { margin-bottom: 10px; }
    th, td { text-align: left; }
    .card { border: 1px solid #999; padding: 6px; margin-bottom: 8px; page-break-inside: avoid; }
    .card-header, .card-title { font-weight: bold; margin-bottom: 4px; }
    .badge { border: 1px solid #666; padding: 0 4px; }
    .btn, button, input, select, .form-control, .form-select, .fa { display: none !important; }
    h1, h2, h3, h4, h5, h6 { page-break-after: avoid; }
</style>
</head>
<body>
    ${sheetHead(title)}
    ${node.innerHTML}
</body>
</html>`;

    printHtmlDocument(html);
}

// Prints a report that only exists as an on-screen grid: the rows are rebuilt
// as a plain table so every row prints, not just the ones the grid has drawn.
export function printDataTable({ title, meta = [], columns, rows, orientation = "portrait", emptyText = "No records found" }) {
    const head = columns
        .map((col) => `<th style="text-align:${col.align || "left"}">${escapeHtml(col.header)}</th>`)
        .join("");

    const list = Array.isArray(rows) ? rows : [];
    const body = list.length
        ? list
              .map((row, index) => {
                  const cells = columns
                      .map((col) => {
                          const value = typeof col.value === "function" ? col.value(row, index) : row[col.field];
                          return `<td style="text-align:${col.align || "left"}">${escapeHtml(value)}</td>`;
                      })
                      .join("");
                  return `<tr>${cells}</tr>`;
              })
              .join("")
        : `<tr><td colspan="${columns.length}" style="text-align:center">${escapeHtml(emptyText)}</td></tr>`;

    const metaLines = meta
        .filter((line) => line !== null && line !== undefined && line !== "")
        .map((line) => `<div>${escapeHtml(line)}</div>`)
        .join("");

    const html = `<!DOCTYPE html>
<html>
<head>
<title>${escapeHtml(title)}</title>
<style>
    @page { size: A4 ${orientation}; }
    ${BASE_STYLES}
    .meta { text-align: center; margin-bottom: 10px; font-size: 11px; }
</style>
</head>
<body>
    ${sheetHead(title)}
    <div class="meta">${metaLines}</div>
    <table>
        <thead><tr>${head}</tr></thead>
        <tbody>${body}</tbody>
    </table>
</body>
</html>`;

    printHtmlDocument(html);
}

export default printDataTable;
